import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useGetGeoSchemeQuery } from '@shared/api';
import {
  apiErrorMessage,
  fmtDate,
  fmtDateTime,
  workTypeShort,
  workTypeTitle,
} from '@entities/geodesy';
import { useAgentPermission } from '@entities/user';
import {
  AppliedTolerance,
  ChecksList,
  DeviationTable,
  DeviationTotals,
  DeviationWarnings,
  ErrorText,
  Eyebrow,
  latestVersion,
  Muted,
  Notice,
  Panel,
  PanelHead,
  PanelTitle,
  RequestBriefLink,
  SchemeActions,
  SchemeJournal,
  SchemeStatusBadge,
  Stack,
  useGeoObjectNames,
  VersionDownloads,
  VersionPreview,
} from '@widgets/GeoSchemes';

const Wrap = styled.div`
  container-type: inline-size;
  container-name: pto-scheme;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const Head = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(6)}`};
  align-items: start;
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(6)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  @container pto-scheme (max-width: 760px) {
    grid-template-columns: 1fr;
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

const QuietLink = styled(Link)`
  font-size: 13px;
  font-weight: 600;
`;

const SchemeNo = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  letter-spacing: 0;
  text-transform: none;
`;

const Title = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(1.5)} 0 ${theme.spacing(2)}`};
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.25;
  text-wrap: balance;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @container pto-scheme (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/**
 * Карточка ИС для инженера ПТО: превью и файлы текущей версии, сводка отклонений,
 * контроль ГОСТ, журнал и действия по статусу (принять / отклонить / заказчик).
 */
export function PtoExecSchemeCard() {
  const { id = '' } = useParams<{ id: string }>();
  const {
    data: scheme,
    isLoading,
    error,
  } = useGetGeoSchemeQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: 30,
    pollingInterval: 60_000,
  });
  const names = useGeoObjectNames();
  const geodesy = useAgentPermission('geodesy');

  if (isLoading) return <Muted>Загрузка схемы…</Muted>;
  if (error || !scheme) {
    return (
      <ErrorText role="alert">
        {apiErrorMessage(error) ?? 'Схема не найдена или нет доступа.'}
      </ErrorText>
    );
  }

  const v = latestVersion(scheme);
  const report = scheme.deviations;
  const objectName = scheme.object_name || names.project(scheme.project_id);
  const site = names.site(scheme.site_id);

  return (
    <Wrap>
      <Head>
        <div>
          <Eyebrow>
            Исполнительная схема · <SchemeNo>{scheme.number}</SchemeNo>
          </Eyebrow>
          <Title>{scheme.title}</Title>
          <Meta>
            <SchemeStatusBadge status={scheme.status} rejectedBy={scheme.rejected_by} />
            <span>
              {workTypeShort(scheme.work_type)} — {workTypeTitle(scheme.work_type).toLowerCase()}
            </span>
            {objectName && <span>{objectName}</span>}
            {site && <span>площадка: {site}</span>}
            {scheme.created_by_name && <span>геодезист: {scheme.created_by_name}</span>}
            <span>обновлена {fmtDateTime(scheme.updated_at)}</span>
          </Meta>
          {scheme.request && <RequestBriefLink request={scheme.request} />}
        </div>
        <Stack $gap={2}>
          <SchemeActions scheme={scheme} actor="engineer" />
          {geodesy.can_read && (
            <QuietLink to={`/agents/geodesy/schemes/${scheme.id}`}>
              Открыть в ОСНОВА.Геодезист →
            </QuietLink>
          )}
        </Stack>
      </Head>

      {scheme.status === 'rejected' && (
        <Notice $tone="danger" role="status">
          <strong>
            {scheme.rejected_by === 'customer' ? 'Отклонена заказчиком' : 'Отклонена ПТО'} — схема у
            геодезиста на корректировке
          </strong>
          {scheme.reject_reason && <span>Причина: {scheme.reject_reason}</span>}
        </Notice>
      )}
      {scheme.status === 'approved' && (
        <Notice $tone="success" role="status">
          Согласована заказчиком — схема готова к подшивке в исполнительную документацию.
        </Notice>
      )}
      {!scheme.permissions.can_review && scheme.status !== 'approved' && (
        <Muted>Решения по схеме принимает инженер ПТО с правом записи.</Muted>
      )}

      <Columns>
        <Stack $gap={5}>
          <Panel aria-labelledby="pto-scheme-sheet">
            <PanelHead>
              <PanelTitle id="pto-scheme-sheet">
                {v ? `Версия ${v.version}` : 'Схема ещё не сформирована'}
              </PanelTitle>
              {v && <VersionDownloads scheme={scheme} version={v} />}
            </PanelHead>
            {v ? (
              <VersionPreview scheme={scheme} version={v} />
            ) : (
              <Muted>Геодезист ещё не выпустил ни одной версии.</Muted>
            )}
          </Panel>

          <Panel aria-labelledby="pto-scheme-dev">
            <PanelHead>
              <PanelTitle id="pto-scheme-dev">Отклонения от проекта</PanelTitle>
              {report && <Muted>расчёт {fmtDateTime(report.computed_at)}</Muted>}
            </PanelHead>
            {report ? (
              <Stack $gap={4}>
                <DeviationTotals report={report} />
                <DeviationWarnings report={report} />
                {report.totals.exceeded === 0 ? (
                  <Notice $tone="success" role="status">
                    Отклонения от проекта по геометрическим параметрам в пределах допусков.
                  </Notice>
                ) : scheme.settings.agreement ? (
                  <Notice $tone="info" role="status">
                    Превышения согласованы: {scheme.settings.agreement.organization}, документ{' '}
                    {scheme.settings.agreement.document} от{' '}
                    {fmtDate(scheme.settings.agreement.date)}.
                  </Notice>
                ) : (
                  <Notice $tone="warning" role="status">
                    Есть превышения допусков, согласование с проектировщиком не указано (ГОСТ Р
                    51872 п.5.5).
                  </Notice>
                )}
                <AppliedTolerance report={report} />
                <DeviationTable report={report} />
              </Stack>
            ) : (
              <Muted>Отклонения не рассчитаны.</Muted>
            )}
          </Panel>

          {scheme.checks.length > 0 && (
            <Panel aria-labelledby="pto-scheme-checks">
              <PanelHead>
                <PanelTitle id="pto-scheme-checks">Контроль ГОСТ Р 51872</PanelTitle>
              </PanelHead>
              <ChecksList checks={scheme.checks} />
            </Panel>
          )}
        </Stack>

        <Panel aria-labelledby="pto-scheme-journal">
          <PanelHead>
            <PanelTitle id="pto-scheme-journal">Журнал и обсуждение</PanelTitle>
          </PanelHead>
          <SchemeJournal
            scheme={scheme}
            canComment={scheme.permissions.can_review || scheme.permissions.can_edit}
          />
        </Panel>
      </Columns>
    </Wrap>
  );
}
